import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { stakeDex, swap, swapETHForToken, unstakeDex, getPositionValue, getSwapFeeRate } from "./CrownDexContract";
import { formatEther, parseEther, formatUnits } from "ethers";
import CrownDexABI from "../abi/CrownDex.json";
import CrownABI from "../abi/Crown.json";

const AggregatorV3InterfaceABI = [
  {
    "inputs": [],
    "name": "latestRoundData",
    "outputs": [
      { "internalType": "uint80", "name": "roundId", "type": "uint80" },
      { "internalType": "int256", "name": "answer", "type": "int256" },
      { "internalType": "uint256", "name": "startedAt", "type": "uint256" },
      { "internalType": "uint256", "name": "updatedAt", "type": "uint256" },
      { "internalType": "uint80", "name": "answeredInRound", "type": "uint80" }
    ],
    "stateMutability": "view",
    "type": "function"
  }
];

const CrownDex = ({ provider, account, onTransaction }) => {
  const [tokenIn, setTokenIn] = useState("");
  const [tokenOut, setTokenOut] = useState("");
  const [amountIn, setAmountIn] = useState("");
  const [minAmountOut, setMinAmountOut] = useState("");
  const [ethAmount, setEthAmount] = useState("");
  const [token0, setToken0] = useState("");
  const [token1, setToken1] = useState("");
  const [amount0, setAmount0] = useState("");
  const [amount1, setAmount1] = useState("");
  const [priceLower, setPriceLower] = useState("");
  const [priceUpper, setPriceUpper] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [positionValue, setPositionValue] = useState(null);
  const [positionDetails, setPositionDetails] = useState(null);
  const [crwPrice, setCrwPrice] = useState("0");
  const [swapFeeRate, setSwapFeeRate] = useState("0");
  const [priceSource, setPriceSource] = useState("Fixed");
  const [errorMessage, setErrorMessage] = useState("");
  const [approveAddress, setApproveAddress] = useState("");
  const [notification, setNotification] = useState("");
  const [crwBalance, setCrwBalance] = useState("0"); // Nový stav pro zůstatek CRW

  const tokens = [
    { symbol: "CRW", address: "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03", img: "https://assets.coingecko.com/coins/images/12335/small/crown.png" },
    { symbol: "CUSD", address: "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa", img: "" },
    { symbol: "USDT", address: "0xdAC17F958D2ee523a2206206994597C13D831ec7", img: "https://assets.coingecko.com/coins/images/325/small/Tether.png" },
    { symbol: "USDC", address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", img: "https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png" },
    { symbol: "WETH", address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", img: "https://assets.coingecko.com/coins/images/2518/small/weth.png" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setErrorMessage("");
        if (!provider) {
          throw new Error("Provider is not available.");
        }
        const signer = await provider.getSigner();

        const crownContract = new ethers.Contract(
          "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03",
          CrownABI,
          signer
        );

        const crownDexContract = new ethers.Contract(
          "0x20f2238d242888e3b9fe1f7ef7e800f326e3718d",
          CrownDexABI,
          signer
        );

        // Načtení zůstatku CRW tokenů uživatele
        if (account) {
          const balance = await crownContract.balanceOf(account);
          setCrwBalance(formatEther(balance));
        } else {
          setCrwBalance("0");
        }

        const useOracle = await crownContract.useOraclePrice();
        let price;

        if (useOracle) {
          const crwPriceFeedAddress = await crownDexContract.crwPriceFeed();
          const priceFeedContract = new ethers.Contract(
            crwPriceFeedAddress,
            AggregatorV3InterfaceABI,
            signer
          );
          const roundData = await priceFeedContract.latestRoundData();
          price = roundData.answer;
          price = ethers.formatUnits(price, 8);
          setPriceSource("Oracle");
        } else {
          price = await crownContract.tokenPriceBase();
          price = ethers.formatEther(price);
          setPriceSource("Fixed");
        }

        const feeRate = await getSwapFeeRate(signer);

        setCrwPrice(price.toString());
        setSwapFeeRate(formatUnits(feeRate, 2));
      } catch (error) {
        console.error("Error fetching data:", error);
        setCrwPrice("0");
        setSwapFeeRate("0");
        setPriceSource("Error");
        setCrwBalance("0");
        setErrorMessage("Failed to fetch data. Please ensure your wallet is connected and try again.");
      }
    };

    const setupEventListeners = async () => {
      try {
        if (!provider) {
          throw new Error("Provider is not available.");
        }
        const contractWithProvider = new ethers.Contract(
          "0x20f2238d242888e3b9fe1f7ef7e800f326e3718d",
          CrownDexABI,
          provider
        );

        // Posluchač pro TokenSwapped (přidána kontrola account)
        contractWithProvider.on("TokenSwapped", (user, tokenIn, tokenOut, amountIn, amountOut, fee, event) => {
          if (account && user.toLowerCase() === account.toLowerCase()) {
            const totalFee = formatEther(fee);
            setNotification(`Swap completed! Total fee: ${totalFee} CRW`);
          }
        });

        // Posluchač pro FeeDistributed (přidána kontrola account)
        contractWithProvider.on("FeeDistributed", (stakerFeeShare, validatorFeeShare, event) => {
          // Nemůžeme přímo filtrovat podle account, protože FeeDistributed neobsahuje adresu uživatele,
          // ale můžeme předpokládat, že následuje po TokenSwapped
          const validatorFees = formatEther(validatorFeeShare);
          setNotification((prev) => `${prev} | Validators received: ${validatorFees} CRW`);
        });

        return () => {
          contractWithProvider.removeAllListeners("TokenSwapped");
          contractWithProvider.removeAllListeners("FeeDistributed");
        };
      } catch (error) {
        console.error("Error setting up event listeners:", error.message);
      }
    };

    if (provider) {
      fetchData();
      setupEventListeners();
    }

    return () => {};
  }, [provider, account]); // Přidána závislost na account

  const handleSwap = async () => {
    if (!account) {
      setErrorMessage("Please connect your wallet first!");
      return;
    }
    try {
      setErrorMessage("");
      setNotification("");
      const signer = await provider.getSigner();
      const tx = await swap(signer, tokenIn, tokenOut, parseEther(amountIn), parseEther(minAmountOut));
      const receipt = await tx.wait();
      const transaction = {
        type: "Swap",
        tokenIn: tokens.find((t) => t.address === tokenIn)?.symbol,
        tokenOut: tokens.find((t) => t.address === tokenOut)?.symbol,
        amountIn,
        amountOut: formatEther(receipt.events.find((e) => e.event === "TokenSwapped")?.args.amountOut || "0"),
        fee: formatEther(receipt.events.find((e) => e.event === "TokenSwapped")?.args.fee || "0"),
        timestamp: Date.now(),
      };
      if (onTransaction) onTransaction(transaction);
      // Aktualizace zůstatku po swapu
      const crownContract = new ethers.Contract(
        "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03",
        CrownABI,
        await provider.getSigner()
      );
      const balance = await crownContract.balanceOf(account);
      setCrwBalance(formatEther(balance));
      alert("Swap successful!");
    } catch (error) {
      console.error("Swap failed:", error);
      setErrorMessage("Swap failed: " + (error.message || "Unknown error"));
    }
  };

  const handleSwapETH = async () => {
    if (!account) {
      setErrorMessage("Please connect your wallet first!");
      return;
    }
    try {
      setErrorMessage("");
      setNotification("");
      const signer = await provider.getSigner();
      const tx = await swapETHForToken(signer, tokenOut, parseEther(minAmountOut), parseEther(ethAmount));
      const receipt = await tx.wait();
      const transaction = {
        type: "Swap ETH",
        tokenOut: tokens.find((t) => t.address === tokenOut)?.symbol,
        ethAmount,
        amountOut: formatEther(receipt.events.find((e) => e.event === "TokenSwapped")?.args.amountOut || "0"),
        fee: formatEther(receipt.events.find((e) => e.event === "TokenSwapped")?.args.fee || "0"),
        timestamp: Date.now(),
      };
      if (onTransaction) onTransaction(transaction);
      // Aktualizace zůstatku po swapu
      const crownContract = new ethers.Contract(
        "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03",
        CrownABI,
        await provider.getSigner()
      );
      const balance = await crownContract.balanceOf(account);
      setCrwBalance(formatEther(balance));
      alert("ETH Swap successful!");
    } catch (error) {
      console.error("ETH Swap failed:", error);
      setErrorMessage("ETH Swap failed: " + (error.message || "Unknown error"));
    }
  };

  const handleStake = async () => {
    if (!account) {
      setErrorMessage("Please connect your wallet first!");
      return;
    }
    try {
      setErrorMessage("");
      setNotification("");
      const signer = await provider.getSigner();
      const tx = await stakeDex(signer, token0, token1, parseEther(amount0), parseEther(amount1), parseEther(priceLower), parseEther(priceUpper));
      const receipt = await tx.wait();
      const transaction = {
        type: "Stake",
        token0: tokens.find((t) => t.address === token0)?.symbol,
        token1: tokens.find((t) => t.address === token1)?.symbol,
        amount0,
        amount1,
        tokenId: receipt.events.find((e) => e.event === "StakedDex")?.args.tokenId.toString() || "N/A",
        timestamp: Date.now(),
      };
      if (onTransaction) onTransaction(transaction);
      // Aktualizace zůstatku po stake
      const crownContract = new ethers.Contract(
        "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03",
        CrownABI,
        await provider.getSigner()
      );
      const balance = await crownContract.balanceOf(account);
      setCrwBalance(formatEther(balance));
      alert("Stake successful!");
    } catch (error) {
      console.error("Stake failed:", error);
      setErrorMessage("Stake failed: " + (error.message || "Unknown error"));
    }
  };

  const handleUnstake = async () => {
    if (!account) {
      setErrorMessage("Please connect your wallet first!");
      return;
    }
    try {
      setErrorMessage("");
      setNotification("");
      const signer = await provider.getSigner();
      const tx = await unstakeDex(signer, tokenId);
      const receipt = await tx.wait();
      const transaction = {
        type: "Unstake",
        tokenId,
        amount0: formatEther(receipt.events.find((e) => e.event === "UnstakedDex")?.args.amount0 || "0"),
        amount1: formatEther(receipt.events.find((e) => e.event === "UnstakedDex")?.args.amount1 || "0"),
        reward: formatEther(receipt.events.find((e) => e.event === "UnstakedDex")?.args.reward || "0"),
        timestamp: Date.now(),
      };
      if (onTransaction) onTransaction(transaction);
      // Aktualizace zůstatku po unstake
      const crownContract = new ethers.Contract(
        "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03",
        CrownABI,
        await provider.getSigner()
      );
      const balance = await crownContract.balanceOf(account);
      setCrwBalance(formatEther(balance));
      alert("Unstake successful!");
    } catch (error) {
      console.error("Unstake failed:", error);
      setErrorMessage("Unstake failed: " + (error.message || "Unknown error"));
    }
  };

  const checkPositionValue = async () => {
    if (!account) {
      setErrorMessage("Please connect your wallet first!");
      return;
    }
    try {
      setErrorMessage("");
      setNotification("");
      const signer = await provider.getSigner();
      const value = await getPositionValue(signer, tokenId);
      setPositionValue(formatEther(value));
    } catch (error) {
      console.error("Get position value failed:", error);
      setErrorMessage("Failed to get position value: " + (error.message || "Unknown error"));
    }
  };

  const getPositionDetails = async () => {
    if (!account) {
      setErrorMessage("Please connect your wallet first!");
      return;
    }
    try {
      setErrorMessage("");
      setNotification("");
      const signer = await provider.getSigner();
      const contract = new ethers.Contract("0x20f2238d242888e3b9fe1f7ef7e800f326e3718d", CrownDexABI, signer);
      const details = await contract.positions(tokenId);
      setPositionDetails({
        token0: details.token0,
        token1: details.token1,
        amount0: formatEther(details.amount0),
        amount1: formatEther(details.amount1),
        priceLower: formatEther(details.priceLower),
        priceUpper: formatEther(details.priceUpper),
        accumulatedFees0: formatEther(details.accumulatedFees0),
        accumulatedFees1: formatEther(details.accumulatedFees1),
      });
    } catch (error) {
      console.error("Get position details failed:", error);
      setErrorMessage("Failed to get position details: " + (error.message || "Unknown error"));
    }
  };

  const handleApprove = async () => {
    if (!account) {
      setErrorMessage("Please connect your wallet first!");
      return;
    }
    try {
      setErrorMessage("");
      setNotification("");
      const signer = await provider.getSigner();
      const contract = new ethers.Contract("0x20f2238d242888e3b9fe1f7ef7e800f326e3718d", CrownDexABI, signer);
      const tx = await contract.approve(approveAddress, tokenId);
      await tx.wait();
      alert("Approval successful!");
      setApproveAddress("");
      setTokenId("");
    } catch (error) {
      console.error("Approval failed:", error);
      setErrorMessage("Approval failed: " + (error.message || "Unknown error"));
    }
  };

  // Kontrola připojení peněženky
  if (!account) {
    return (
      <div className="crowndex">
        <h2>CrownDex</h2>
        <p style={{ color: "red" }}>Please connect your wallet to use CrownDex.</p>
      </div>
    );
  }

  return (
    <div className="crowndex">
      <h2>CrownDex</h2>

      <div className="price-info">
        <p>Account: {account}</p> {/* Zobrazení adresy uživatele */}
        <p>CRW Balance: {crwBalance} CRW</p> {/* Zobrazení zůstatku */}
        <p>CRW Price: {crwPrice} ETH</p>
        <p>Price Source: {priceSource}</p>
        <p>Swap Fee Rate: {swapFeeRate}%</p>
        {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
        {notification && <p style={{ color: "green" }}>{notification}</p>}
      </div>

      <div className="swap-section">
        <h3>Swap Tokens</h3>
        <select onChange={(e) => setTokenIn(e.target.value)} value={tokenIn}>
          <option value="">Select Token In</option>
          {tokens.map((token) => (
            <option key={token.address} value={token.address}>
              {token.symbol}
            </option>
          ))}
        </select>
        <select onChange={(e) => setTokenOut(e.target.value)} value={tokenOut}>
          <option value="">Select Token Out</option>
          {tokens.map((token) => (
            <option key={token.address} value={token.address}>
              {token.symbol}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Amount In"
          value={amountIn}
          onChange={(e) => setAmountIn(e.target.value)}
        />
        <input
          type="text"
          placeholder="Min Amount Out"
          value={minAmountOut}
          onChange={(e) => setMinAmountOut(e.target.value)}
        />
        <button onClick={handleSwap} disabled={!tokenIn || !tokenOut || !amountIn || !minAmountOut}>
          Swap
        </button>
      </div>

      <div className="swap-eth-section">
        <h3>Swap ETH for Token</h3>
        <select onChange={(e) => setTokenOut(e.target.value)} value={tokenOut}>
          <option value="">Select Token Out</option>
          {tokens.map((token) => (
            <option key={token.address} value={token.address}>
              {token.symbol}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="ETH Amount"
          value={ethAmount}
          onChange={(e) => setEthAmount(e.target.value)}
        />
        <input
          type="text"
          placeholder="Min Amount Out"
          value={minAmountOut}
          onChange={(e) => setMinAmountOut(e.target.value)}
        />
        <button onClick={handleSwapETH} disabled={!tokenOut || !ethAmount || !minAmountOut}>
          Swap ETH
        </button>
      </div>

      <div className="stake-section">
        <h3>Stake Liquidity</h3>
        <select onChange={(e) => setToken0(e.target.value)} value={token0}>
          <option value="">Select Token 0</option>
          {tokens.map((token) => (
            <option key={token.address} value={token.address}>
              {token.symbol}
            </option>
          ))}
        </select>
        <select onChange={(e) => setToken1(e.target.value)} value={token1}>
          <option value="">Select Token 1</option>
          {tokens.map((token) => (
            <option key={token.address} value={token.address}>
              {token.symbol}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Amount 0"
          value={amount0}
          onChange={(e) => setAmount0(e.target.value)}
        />
        <input
          type="text"
          placeholder="Amount 1"
          value={amount1}
          onChange={(e) => setAmount1(e.target.value)}
        />
        <input
          type="text"
          placeholder="Price Lower"
          value={priceLower}
          onChange={(e) => setPriceLower(e.target.value)}
        />
        <input
          type="text"
          placeholder="Price Upper"
          value={priceUpper}
          onChange={(e) => setPriceUpper(e.target.value)}
        />
        <button onClick={handleStake} disabled={!token0 || !token1 || !amount0 || !amount1 || !priceLower || !priceUpper}>
          Stake
        </button>
      </div>

      <div className="unstake-section">
        <h3>Unstake Liquidity</h3>
        <input
          type="text"
          placeholder="Token ID"
          value={tokenId}
          onChange={(e) => setTokenId(e.target.value)}
        />
        <button onClick={handleUnstake} disabled={!tokenId}>Unstake</button>
        <button onClick={checkPositionValue} disabled={!tokenId}>Check Position Value</button>
        <button onClick={getPositionDetails} disabled={!tokenId}>Get Position Details</button>
        {positionValue && <p>Position Value: {positionValue} Token0</p>}
        {positionDetails && (
          <div>
            <p>Token0: {positionDetails.token0}</p>
            <p>Token1: {positionDetails.token1}</p>
            <p>Amount0: {positionDetails.amount0}</p>
            <p>Amount1: {positionDetails.amount1}</p>
            <p>Price Lower: {positionDetails.priceLower}</p>
            <p>Price Upper: {positionDetails.priceUpper}</p>
            <p>Accumulated Fees0: {positionDetails.accumulatedFees0}</p>
            <p>Accumulated Fees1: {positionDetails.accumulatedFees1}</p>
          </div>
        )}
      </div>

      <div className="approve-section">
        <h3>Approve NFT</h3>
        <input
          type="text"
          placeholder="Token ID"
          value={tokenId}
          onChange={(e) => setTokenId(e.target.value)}
        />
        <input
          type="text"
          placeholder="Approve To Address"
          value={approveAddress}
          onChange={(e) => setApproveAddress(e.target.value)}
        />
        <button onClick={handleApprove} disabled={!tokenId || !approveAddress}>Approve</button>
      </div>
    </div>
  );
};

export default CrownDex;